// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\B2BAnalytics.jsx
// Название: B2B Analytics Dashboard
// Версия: 1.0.0
// Дата: 28.12.2025

import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

const B2BAnalytics = () => {
  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    totalDistributed: 0,
    totalActivated: 0,
    totalSpent: 0,
    activationRate: 0,
    spentRate: 0,
    avgActivationTime: 0,
    recipientCount: 0
  });
  const [campaigns, setCampaigns] = useState([]);
  const [topCampaigns, setTopCampaigns] = useState([]);

  // ============================================
  // LOAD DATA
  // ============================================
  useEffect(() => {
    loadAnalytics();
  }, [period]);

const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Загружаем B2B статистику
      const statsResponse = await api.get('/brand-gifts/b2b-stats');
      const stats = statsResponse.stats || {};  // Данные напрямую в statsResponse.stats
      
      // Загружаем B2B кампании
      const campaignsResponse = await api.get('/campaigns?type=b2b');
      const campaignsData = campaignsResponse.data || [];
      
      // Обогащаем данные кампаний (для совместимости)
      const enrichedCampaigns = campaignsData.map(c => {
        const distributed = parseFloat(c.total_amount || c.reward_amount * (c.recipients_count || 0)) || 0;
        const activated = parseFloat(c.claimed_amount || 0);
        const spent = parseFloat(c.spent_amount || 0);
        const recipients = parseInt(c.recipients_count) || 0;
        const claimed = parseInt(c.claimed_count) || 0;
        
        return {
          ...c,
          distributed,
          activated,
          spent,
          recipients,
          claimed,
          activationRate: recipients > 0 ? Math.round((claimed / recipients) * 100) : 0
        };
      });
      
      console.log('API Stats received:', stats);
      console.log('Campaigns data:', campaignsData.length);
      
      // Обновляем статистику из API
      const newStats = {
        totalCampaigns: campaignsData.length,
        totalDistributed: stats.totalAmount || 0,
        totalActivated: stats.claimedAmount || 0,
        totalSpent: stats.totalAmount || 0,
        activationRate: stats.claimRate || 0,
        spentRate: 100, // Для B2B всегда 100% потрачено при активации
        avgActivationTime: 24, // TODO: рассчитать из данных
        recipientCount: stats.totalGifts || 0
      };
      
      console.log('Setting stats to:', newStats);
      setStats(newStats);
      
      setCampaigns(enrichedCampaigns);
      setTopCampaigns(enrichedCampaigns.slice(0, 5));
      
    } catch (error) {
      console.error('Error loading B2B analytics:', error);
    } finally {
      setLoading(false);
    }
  };
      
  // ============================================
  // HELPERS
  // ============================================
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toFixed(0);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'ready': case 'active': return '#10B981';
      case 'draft': return '#f59e0b';
      case 'completed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  // ============================================
  // STYLES
  // ============================================
  const styles = {
    container: { padding: 24, maxWidth: 1400, margin: '0 auto' },
    
    header: { marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: '#111827', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 12 },
    subtitle: { color: '#6b7280', fontSize: 14, margin: 0 },
    
    // Period selector
    periodBar: { display: 'flex', gap: 8, marginBottom: 24 },
    periodBtn: { 
      padding: '8px 16px', 
      background: '#f3f4f6', 
      border: 'none', 
      borderRadius: 8, 
      fontSize: 13, 
      fontWeight: 500,
      cursor: 'pointer',
      color: '#6b7280'
    },
    periodBtnActive: { background: '#10B981', color: 'white' },
    
    // Stats grid
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
    statCard: { 
      background: 'white', 
      borderRadius: 16, 
      padding: 24, 
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      position: 'relative',
      overflow: 'hidden'
    },
    statIcon: { fontSize: 32, marginBottom: 12 },
    statValue: { fontSize: 32, fontWeight: 700, marginBottom: 4 },
    statLabel: { fontSize: 14, color: '#6b7280' },
    statChange: { fontSize: 12, marginTop: 8 },
    statChangeUp: { color: '#10B981' },
    statChangeDown: { color: '#ef4444' },
    
    // Main content
    contentGrid: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 },
    
    // Card
    card: { 
      background: 'white', 
      borderRadius: 16, 
      padding: 24, 
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      marginBottom: 24
    },
    cardTitle: { fontSize: 16, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 },
    
    // Progress bar
    progressContainer: { marginBottom: 16 },
    progressLabel: { display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 },
    progressBar: { height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' },
    progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
    
    // Table
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid #e5e7eb', fontSize: 12, color: '#6b7280', fontWeight: 600 },
    td: { padding: '12px 8px', borderBottom: '1px solid #f3f4f6', fontSize: 14 },
    
    // Campaign row
    campaignName: { fontWeight: 500, color: '#111827' },
    badge: { padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500 },
    
    // Empty state
    empty: { textAlign: 'center', padding: 40, color: '#6b7280' },
    
    // Loading
    loading: { textAlign: 'center', padding: 60, color: '#6b7280' }
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) {
    return <div style={styles.loading}>⏳ Загрузка аналитики...</div>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          📊 B2B Аналитика
        </h1>
        <p style={styles.subtitle}>
          Статистика по всем B2B кампаниям для сотрудников
        </p>
      </div>

      {/* Period Selector */}
      <div style={styles.periodBar}>
        {[
          { key: '7d', label: '7 дней' },
          { key: '30d', label: '30 дней' },
          { key: '90d', label: '90 дней' },
          { key: 'all', label: 'Всё время' }
        ].map(p => (
          <button
            key={p.key}
            style={{...styles.periodBtn, ...(period === p.key ? styles.periodBtnActive : {})}}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>🎁</div>
          <div style={{...styles.statValue, color: '#10B981'}}>{formatNumber(stats.totalDistributed)}</div>
          <div style={styles.statLabel}>DAR Распределено</div>
          <div style={{...styles.statChange, ...styles.statChangeUp}}>
            📊 {stats.totalCampaigns} кампаний
          </div>
        </div>
        
        <div style={styles.statCard}>
          <div style={styles.statIcon}>✅</div>
          <div style={{...styles.statValue, color: '#3b82f6'}}>{stats.activationRate}%</div>
          <div style={styles.statLabel}>Активировано</div>
          <div style={{...styles.statChange, ...styles.statChangeUp}}>
            👥 {stats.recipientCount} получателей
          </div>
        </div>
        
        <div style={styles.statCard}>
          <div style={styles.statIcon}>💳</div>
          <div style={{...styles.statValue, color: '#8b5cf6'}}>{stats.spentRate}%</div>
          <div style={styles.statLabel}>Потрачено</div>
          <div style={{...styles.statChange}}>
            💰 {formatNumber(stats.totalSpent)} DAR
          </div>
        </div>
        
        <div style={styles.statCard}>
          <div style={styles.statIcon}>⏱️</div>
          <div style={styles.statValue}>{stats.avgActivationTime}ч</div>
          <div style={styles.statLabel}>Среднее время активации</div>
          <div style={{...styles.statChange, ...styles.statChangeUp}}>
            ↓ быстрее на 12%
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.contentGrid}>
        {/* Left Column - Campaign Performance */}
        <div>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📈 Воронка конверсии</h3>
            
            <div style={styles.progressContainer}>
              <div style={styles.progressLabel}>
                <span>Распределено</span>
                <span>{formatNumber(stats.totalDistributed)} DAR (100%)</span>
              </div>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: '100%', background: '#10B981'}} />
              </div>
            </div>
            
            <div style={styles.progressContainer}>
              <div style={styles.progressLabel}>
                <span>Активировано</span>
                <span>{formatNumber(stats.totalActivated)} DAR ({stats.activationRate}%)</span>
              </div>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: stats.activationRate + '%', background: '#3b82f6'}} />
              </div>
            </div>
            
            <div style={styles.progressContainer}>
              <div style={styles.progressLabel}>
                <span>Потрачено</span>
                <span>{formatNumber(stats.totalSpent)} DAR ({stats.spentRate}%)</span>
              </div>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: stats.spentRate + '%', background: '#8b5cf6'}} />
              </div>
            </div>
          </div>

          {/* All Campaigns Table */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📋 Все B2B кампании</h3>
            
            {campaigns.length === 0 ? (
              <div style={styles.empty}>
                <p>Нет B2B кампаний</p>
              </div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Кампания</th>
                    <th style={styles.th}>Получателей</th>
                    <th style={styles.th}>Распределено</th>
                    <th style={styles.th}>Активация</th>
                    <th style={styles.th}>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(campaign => (
                    <tr key={campaign.id}>
                      <td style={styles.td}>
                        <span style={styles.campaignName}>{campaign.name}</span>
                      </td>
                      <td style={styles.td}>{campaign.recipients}</td>
                      <td style={styles.td}>{formatNumber(campaign.distributed)} DAR</td>
                      <td style={styles.td}>
                        <span style={{color: campaign.activationRate >= 50 ? '#10B981' : '#f59e0b'}}>
                          {campaign.activationRate}%
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          background: getStatusColor(campaign.status) + '20',
                          color: getStatusColor(campaign.status)
                        }}>
                          {campaign.status === 'ready' ? '✅ Активна' : 
                           campaign.status === 'draft' ? '📝 Черновик' : 
                           campaign.status === 'completed' ? '✓ Завершена' : campaign.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right Column - Top Campaigns */}
        <div>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🏆 Топ кампаний</h3>
            
            {topCampaigns.length === 0 ? (
              <div style={styles.empty}>Нет данных</div>
            ) : (
              topCampaigns.map((campaign, index) => (
                <div key={campaign.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 0',
                  borderBottom: index < topCampaigns.length - 1 ? '1px solid #f3f4f6' : 'none'
                }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: index === 0 ? '#fef3c7' : index === 1 ? '#f3f4f6' : index === 2 ? '#fef3c7' : '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: index === 0 ? '#f59e0b' : '#6b7280'
                  }}>
                    {index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{campaign.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {campaign.recipients} получателей
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: '#10B981' }}>
                      {formatNumber(campaign.distributed)} DAR
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {campaign.activationRate}% активация
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Quick Stats */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📌 Быстрая статистика</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ color: '#6b7280' }}>Всего кампаний</span>
                <span style={{ fontWeight: 600 }}>{stats.totalCampaigns}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ color: '#6b7280' }}>Всего получателей</span>
                <span style={{ fontWeight: 600 }}>{stats.recipientCount}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ color: '#6b7280' }}>Средний размер награды</span>
                <span style={{ fontWeight: 600 }}>
                  {stats.recipientCount > 0 ? Math.round(stats.totalDistributed / stats.recipientCount) : 0} DAR
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Активных кампаний</span>
                <span style={{ fontWeight: 600, color: '#10B981' }}>
                  {campaigns.filter(c => c.status === 'ready' || c.status === 'active').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BAnalytics;